import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const ReviewsPanel = ({ restaurantId }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchReviews = useCallback(async () => {
        try {
            // Fetch reviews with menu item details
            const { data, error } = await supabase
                .from('reviews')
                .select(`
                *,
                menu_items ( name, image_url )
            `)
                .eq('restaurant_id', restaurantId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setReviews(data || []);
        } catch (err) {
            console.error("Error fetching reviews:", err);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const deleteReview = async (id) => {
        if (!window.confirm("Are you sure you want to delete this review?")) return;
        try {
            const { error } = await supabase.from('reviews').delete().eq('id', id);
            if (error) throw error;
            setReviews(prev => prev.filter(r => r.id !== id));
        } catch (err) {
            console.error("Error deleting review:", err);
        }
    };

    if (loading) return <div className="text-center py-10 text-gray-400">Loading Reviews...</div>;

    return (
        <div>
            {/* STATS SUMMARY */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
                    <div className="bg-yellow-100 text-yellow-600 p-3 rounded-xl text-2xl">⭐</div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {reviews.length > 0 ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1) : '0.0'}
                        </h3>
                        <p className="text-gray-500 text-xs font-bold uppercase">Average Rating</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
                    <div className="bg-blue-100 text-blue-600 p-3 rounded-xl text-2xl">💬</div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{reviews.length}</h3>
                        <p className="text-gray-500 text-xs font-bold uppercase">Total Reviews</p>
                    </div>
                </div>
            </div>

            {/* REVIEWS LIST */}
            <div className="space-y-4">
                {reviews.length === 0 ? (
                    <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-gray-200">
                        <span className="text-4xl block mb-2">📭</span>
                        <p className="text-gray-400 font-bold">No reviews yet.</p>
                    </div>
                ) : (
                    reviews.map(review => (
                        <div key={review.id} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-6 animate-fade-in group">
                            {/* ITEM IMAGE */}
                            <div className="w-16 h-16 rounded-xl bg-gray-100 shrink-0 overflow-hidden">
                                <img src={review.menu_items?.image_url || 'https://via.placeholder.com/64'} alt="" className="w-full h-full object-cover" />
                            </div>

                            <div className="flex-1">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h3 className="font-bold text-gray-900 leading-tight">{review.menu_items?.name || 'Unknown Item'}</h3>
                                        <p className="text-xs text-gray-400 font-bold">
                                            {new Date(review.created_at).toLocaleDateString()} • {new Date(review.created_at).toLocaleTimeString()}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg border border-yellow-100">
                                        <span className="text-yellow-500 font-bold">★</span>
                                        <span className="font-bold text-gray-900">{review.rating}</span>
                                    </div>
                                </div>

                                <p className="text-gray-600 italic">"{review.comment || 'No comment provided.'}"</p>

                                <div className="mt-4 flex gap-2">
                                    {review.is_verified && (
                                        <span className="text-[10px] font-bold uppercase bg-green-50 text-green-600 px-2 py-1 rounded border border-green-100 flex items-center gap-1">
                                            ✅ Verified Purchase
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* DELETE ACTION */}
                            <div className="md:self-center">
                                <button onClick={() => deleteReview(review.id)} className="text-gray-300 hover:text-red-500 p-2 transition-colors" title="Delete Review">
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ReviewsPanel;
